import { useEffect, useState } from 'react'
import { useParams, Link } from 'react-router-dom'
import SectionHeader from '@/components/ui/SectionHeader'
import ArtifactCard from '@/components/ui/ArtifactCard'
import LoadingSpinner from '@/components/ui/LoadingSpinner'
import { useSEO } from '@/hooks/useSEO'
import type { Archive, PodcastEpisode, YouTubeVideo, Artifact } from '@/types'
import * as api from '@/lib/api'

const archiveColors: Record<string, { bg: string; border: string; text: string }> = {
  treaty: { bg: 'bg-blue-900/20', border: 'border-blue-500/30', text: 'text-blue-300' },
  conflict: { bg: 'bg-red-900/20', border: 'border-red-500/30', text: 'text-red-300' },
  civilization: { bg: 'bg-amber-900/20', border: 'border-amber-500/30', text: 'text-amber-300' },
  origin: { bg: 'bg-emerald-900/20', border: 'border-emerald-500/30', text: 'text-emerald-300' },
  lineage: { bg: 'bg-purple-900/20', border: 'border-purple-500/30', text: 'text-purple-300' },
  classified: { bg: 'bg-slate-900/20', border: 'border-slate-500/30', text: 'text-slate-300' },
  exploration: { bg: 'bg-cyan-900/20', border: 'border-cyan-500/30', text: 'text-cyan-300' },
  mythology: { bg: 'bg-indigo-900/20', border: 'border-indigo-500/30', text: 'text-indigo-300' },
}

export default function ArchiveGatewayPage() {
  const { archiveKey } = useParams<{ archiveKey: string }>()
  const [loading, setLoading] = useState(true)
  const [archive, setArchive] = useState<Archive | null>(null)
  const [podcasts, setPodcasts] = useState<PodcastEpisode[]>([])
  const [videos, setVideos] = useState<YouTubeVideo[]>([])
  const [artifacts, setArtifacts] = useState<Artifact[]>([])

  // SEO
  useSEO({
    title: archive?.name,
    description: archive?.description,
    image: archive?.seal_image_url || (archive ? `/images/archives/${archive.key}.png` : undefined),
    url: archive ? `https://thearchivenetwork.com/archive/${archive.key}` : undefined,
  })

  useEffect(() => {
    async function loadData() {
      if (!archiveKey) return
      try {
        const data = await api.getArchiveContent(archiveKey)
        setArchive(data.archive)
        setPodcasts(data.podcasts.slice(0, 3))
        setVideos(data.videos.slice(0, 3))
        setArtifacts(data.artifacts.slice(0, 4))
      } catch (error) {
        console.error('Failed to load archive data:', error)
      } finally {
        setLoading(false)
      }
    }
    loadData()
  }, [archiveKey])

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <LoadingSpinner size="lg" />
      </div>
    )
  }

  if (!archive) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="text-center">
          <h1 className="font-display text-2xl text-archive-cream">Archive Not Found</h1>
          <Link to="/archives" className="mt-4 btn-secondary inline-block">
            View All Archives
          </Link>
        </div>
      </div>
    )
  }

  const colors = archiveColors[archive.key] || archiveColors.treaty

  return (
    <div className="animate-fade-in">
      {/* Hero */}
      <section className={`py-16 sm:py-24 ${colors.bg}`}>
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          {/* Back link */}
          <Link
            to="/"
            className="inline-flex items-center gap-2 text-sm text-archive-muted hover:text-archive-gold transition-colors mb-8"
          >
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
            </svg>
            Back to The Archive Network
          </Link>
          <div className="flex flex-col md:flex-row items-center gap-8">
            {/* Seal */}
            <div className={`w-32 h-32 sm:w-40 sm:h-40 rounded-full bg-archive-slate border-2 ${colors.border} flex items-center justify-center shadow-xl`}>
              <img
                src={archive.seal_image_url || `/seals/${archive.key}-seal.png`}
                alt={`${archive.name} seal`}
                className="w-full h-full object-cover rounded-full"
              />
            </div>

            {/* Info */}
            <div className="flex-1 text-center md:text-left">
              <span className={`inline-block px-3 py-1 text-xs font-medium uppercase tracking-wider ${colors.bg} ${colors.text} ${colors.border} border rounded mb-4`}>
                Archive Gateway
              </span>
              <h1 className="font-display text-4xl sm:text-5xl text-archive-cream">
                {archive.name}
              </h1>
              <p className="mt-4 text-lg text-archive-muted max-w-2xl">
                {archive.description}
              </p>
              <a
                href={archive.site_url}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-2 mt-6 btn-primary"
              >
                Enter the {archive.name.split(' ')[1]} Archive
                <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 6H6a2 2 0 00-2 2v10a2 2 0 002 2h10a2 2 0 002-2v-4M14 4h6m0 0v6m0-6L10 14" />
                </svg>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Latest Podcasts */}
      {podcasts.length > 0 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader
              title="Latest Podcasts"
              subtitle={`Recent episodes from the ${archive.name}`}
              ctaText="All podcasts"
              ctaLink="/podcasts"
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {podcasts.map((episode) => (
                <ArtifactCard
                  key={episode.id}
                  title={episode.title}
                  description={episode.summary}
                  imageUrl={
                    episode.cover_image_url ||
                    episode.series_cover_image_url ||
                    `/images/podcasts/${episode.archive_key}.jpg`
                  }
                  linkTo={`/podcast/${episode.id}`}
                  type="podcast"
                  archiveKey={episode.archive_key}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Latest Videos */}
      {videos.length > 0 && (
        <section className="py-16 bg-archive-charcoal/30">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader
              title="Latest Conversations"
              subtitle={`Recent videos from the ${archive.name}`}
              ctaText="All conversations"
              ctaLink="/conversations"
            />
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {videos.map((video) => (
                <ArtifactCard
                  key={video.id}
                  title={video.title}
                  description={video.summary}
                  imageUrl={video.thumbnail_url}
                  linkTo={`/video/${video.id}`}
                  type="video"
                  archiveKey={video.archive_key}
                />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* Artifacts */}
      {artifacts.length > 0 && (
        <section className="py-16">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
            <SectionHeader
              title="Archive Artifacts"
              subtitle="Collectible prints and medallions from this archive"
              ctaText="Browse all artifacts"
              ctaLink="/artifacts"
            />
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              {artifacts.map((artifact) => (
                <ArtifactCard
                  key={artifact.slug}
                  title={artifact.title}
                  subtitle={artifact.subtitle}
                  imageUrl={artifact.image_url}
                  linkTo={`/artifact/${artifact.slug}`}
                  archiveKey={artifact.archive_key}
                />
              ))}
            </div>
          </div>
        </section>
      )}
    </div>
  )
}
